/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config, URL } from '../util';

type SiginInProps = {
  email: string;
  password: string;
  setIdToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsLogin: React.Dispatch<React.SetStateAction<boolean>>;
  setAccessToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  setErr: React.Dispatch<React.SetStateAction<string | undefined>>;
  history: any;
  setUserName: React.Dispatch<React.SetStateAction<string | undefined>>;
  userName: string;
  setTenantAlias: React.Dispatch<React.SetStateAction<string>>;
  setMfaUser: React.Dispatch<React.SetStateAction<CognitoUser | undefined>>;
};

type SendMfaCodeProps = Omit<SiginInProps, 'email' | 'password'> & {
  cognitoUser: CognitoUser;
  code: string;
};

type FogotPasswordProps = {
  history: any;
  email: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

type ChangePasswordProps = {
  history: any;
  password: string;
  code: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  setErr: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export const userPool = new CognitoUserPool({
  UserPoolId: config.awsConfiguration.UserPoolId,
  ClientId: config.awsConfiguration.ClientId,
});

type LoginSuccessProps = Omit<SiginInProps, 'email' | 'password'> & {
  result: CognitoUserSession;
};

const loginSuccess = ({
  result,
  setAccessToken,
  setIdToken,
  setIsLogin,
  history,
  setUserName,
  userName,
  setTenantAlias,
  setMfaUser,
}: LoginSuccessProps): void => {
  const decodeToken = result.getIdToken().decodePayload();
  setTenantAlias(decodeToken['custom:tenant']);
  setAccessToken(result.getAccessToken().getJwtToken());
  setIdToken(result.getIdToken().getJwtToken());
  setIsLogin(true);
  setUserName(userName);
  setMfaUser(undefined);
  history.push(URL.dashboard);
};

export const CognitoLogin = ({
  email,
  password,
  ...props
}: SiginInProps): void => {
  const { setErr, setMfaUser } = props;
  setErr('');
  const authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });
  cognitoUser.authenticateUser(authenticationDetails, {
    onSuccess: result => {
      loginSuccess({ result, ...props });
    },
    onFailure: err => {
      setErr(errorhandle(err));
    },
    // MFA(TOTP)設定済みユーザーは認証アプリのコード入力へ
    totpRequired: () => {
      setMfaUser(cognitoUser);
    },
  });
};

export const CognitoSendMfaCode = ({
  cognitoUser,
  code,
  ...props
}: SendMfaCodeProps): void => {
  const { setErr, setMfaUser } = props;
  setErr('');
  if (!MFA_CODE_PATTERN.test(code)) {
    setErr(MFA_CODE_FORMAT_ERROR);

    return;
  }
  cognitoUser.sendMFACode(
    code,
    {
      onSuccess: result => {
        loginSuccess({ result, ...props });
      },
      onFailure: err => {
        // セッション切れの場合はID/パスワード入力からやり直す
        if (err.code === 'NotAuthorizedException') {
          setMfaUser(undefined);
        }
        setErr(mfaErrorhandle(err));
      },
    },
    'SOFTWARE_TOKEN_MFA'
  );
};

// ログイン中ユーザー(セッション付き)を取得
export const getSessionUser = (): Promise<CognitoUser> =>
  new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser === null) {
      reject(new Error('not login'));

      return;
    }
    cognitoUser.getSession((error: Error | null) => {
      if (error) {
        reject(error);

        return;
      }
      resolve(cognitoUser);
    });
  });

// MFA(TOTP)が有効か(キャッシュを使わずCognitoから取得)
export const isTotpEnabled = (cognitoUser: CognitoUser): Promise<boolean> =>
  new Promise((resolve, reject) => {
    cognitoUser.getUserData(
      (err, data) => {
        if (err || !data) {
          reject(err);

          return;
        }
        resolve((data.UserMFASettingList ?? []).includes('SOFTWARE_TOKEN_MFA'));
      },
      { bypassCache: true }
    );
  });

// MFA(TOTP)のシークレットキーを発行
export const associateTotp = (cognitoUser: CognitoUser): Promise<string> =>
  new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      associateSecretCode: secretCode => resolve(secretCode),
      onFailure: err => reject(err),
    });
  });

// 認証アプリのコードを検証し、MFA(TOTP)を有効化
export const enableTotp = (
  cognitoUser: CognitoUser,
  code: string
): Promise<void> =>
  new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, 'GrantDrive', {
      onSuccess: () => {
        cognitoUser.setUserMfaPreference(
          null,
          { Enabled: true, PreferredMfa: true },
          err => (err ? reject(err) : resolve())
        );
      },
      onFailure: err => reject(err),
    });
  });

export const FogotPassword = ({
  history,
  email,
  setLoading,
}: FogotPasswordProps): void => {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });
  cognitoUser.forgotPassword({
    onSuccess: () => {
      setLoading(false);
    },
    onFailure: () => {
      setLoading(false);
    },
    inputVerificationCode: () => {
      history.push(URL.passwordChange, { email });
      setLoading(false);
    },
  });
};

export const ChangePassword = ({
  password,
  code,
  setLoading,
  history,
  email,
  setErr,
}: ChangePasswordProps): void => {
  setErr('');
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  cognitoUser.confirmPassword(code, password, {
    onSuccess: () => {
      history.push(URL.passwordChangeDone);
      setLoading(false);
    },
    onFailure: err => {
      setErr(errorhandle(err));
      setLoading(false);
    },
  });
};

export const CognitoLogout = (userName: string, history: any): void => {
  const cognitoUser = new CognitoUser({
    Username: userName,
    Pool: userPool,
  });
  cognitoUser.signOut(() => {
    localStorage.clear();
    history.push(URL.index);
  });
};

// 認証アプリのコード(TOTP)は6桁の数字
export const MFA_CODE_PATTERN = /^\d{6}$/;
export const MFA_CODE_FORMAT_ERROR =
  '認証アプリのコードは6桁の数字で入力してください。';

// 認証アプリのコード入力時のエラー
// パスワードリセットの「認証コード」と区別するため文言を分ける。形式不正(InvalidParameterException)もID/パスワード誤りと表示しない
export const mfaErrorhandle = (err: any): string => {
  switch (err.code) {
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
    case 'InvalidParameterException':
      return '認証アプリのコードが正しくありません。';
    // 同じコードの再利用(有効時間内でも1回のみ使用可)
    case 'ExpiredCodeException':
      return 'このコードは使用済みです。認証アプリに新しいコードが表示されてから入力してください。';
    default:
      return errorhandle(err);
  }
};

export const errorhandle = (err: any): string => {
  switch (err.code) {
    case 'NotAuthorizedException':
      if (err.message === 'Password attempts exceeded.') {
        return '認証が連続して失敗しました。しばらくしてからログインしてください。';
      }
      if (/session/i.test(err.message)) {
        return 'セッションの有効期限が切れました。再度ログインしてください。';
      }

      return 'ユーザー名またはパスワードが違います。';
    case 'UserNotFoundException':
      return 'ユーザー名またはパスワードが違います。';
    case 'InvalidParameterException':
      return 'ユーザー名またはパスワードが違います。';
    case 'InvalidPasswordException':
      return 'パスワードポリシーをご確認のうえ、パスワードを設定してください。';
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return '認証コードが正しくありません。';
    default:
      return 'エラーが発生しました。しばらくしてからお試しください。';
  }
};
